/**
 * Hook for debounced mutations that handles the common pattern of
 * auto-saving form data with optimistic updates.
 */

import { useRef, useCallback, useEffect } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

const DEFAULT_DEBOUNCE_DELAY = 1500;

interface UseDebouncedMutationOptions<TVariables> {
  /** The mutation to debounce */
  mutation: UseMutationResult<unknown, unknown, TVariables, unknown>;
  /** Delay in milliseconds (default: 1500) */
  delay?: number;
  /** Called when mutation starts */
  onMutating?: () => void;
  /** Called when mutation succeeds */
  onSuccess?: () => void;
  /** Called when mutation fails */
  onError?: (error: unknown) => void;
}

/**
 * Wraps a TanStack Query mutation with debounce logic.
 *
 * Usage:
 * ```tsx
 * const updateMutation = useUpdateSimulation(simulationId);
 * const debouncedUpdate = useDebouncedMutation({
 *   mutation: updateMutation,
 *   delay: 1500,
 * });
 *
 * // In onChange handler:
 * debouncedUpdate.mutate({ name: newName });
 * ```
 */
export function useDebouncedMutation<TVariables>({
  mutation,
  delay = DEFAULT_DEBOUNCE_DELAY,
  onMutating,
  onSuccess,
  onError,
}: UseDebouncedMutationOptions<TVariables>) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingVarsRef = useRef<TVariables | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const mutate = useCallback(
    (variables: TVariables) => {
      // Store the latest variables
      pendingVarsRef.current = variables;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        if (pendingVarsRef.current) {
          onMutating?.();
          mutation.mutate(pendingVarsRef.current, {
            onSuccess: () => onSuccess?.(),
            onError: (error) => onError?.(error),
          });
          pendingVarsRef.current = null;
        }
      }, delay);
    },
    [mutation, delay, onMutating, onSuccess, onError],
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingVarsRef.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingVarsRef.current) {
      onMutating?.();
      mutation.mutate(pendingVarsRef.current, {
        onSuccess: () => onSuccess?.(),
        onError: (error) => onError?.(error),
      });
      pendingVarsRef.current = null;
    }
  }, [mutation, onMutating, onSuccess, onError]);

  return {
    mutate,
    cancel,
    flush,
    isPending: mutation.isPending,
  };
}
